import { isNil } from '@/utils/isNil';
import dayjs from 'dayjs';

import {
  BUY_TYPE,
  FINANCE_QUOTE_RADIO_TYPE,
  TERMS_AND_CONDITIONS,
} from 'src/checkout/templates/Checkout/Forms/CheckoutForm.type';
import { AustralianState } from 'src/constants/constants';
import { InputAddressAutocompleteAddress } from 'src/general/components/forms/FormikInputAddressAutocomplete/FormikInputAddressAutocomplete';
import { PersonalisedQuotePayloadPartial } from 'src/personalised-quote/templates/Forms/PersonalizedQuoteForm.type';

import { FINANCE_RESIDENCY_STATUS, FinanceQuoteRequestPayload } from './FinanceApi.types';

const financeAddressToPayloadAddress = (
  address?: FinanceQuoteRequestPayload['applicant']['current_address']['address'],
): InputAddressAutocompleteAddress | null => {
  if (typeof address === 'undefined') {
    return null;
  }
  return {
    apartment: address.unit,
    street: `${address.street_number} ${address.street_name}`,
    suburb: address.suburb,
    postcode: address.postcode,
    state: address.state as AustralianState,
  };
};

const processYearMonth = (value?: string): string | null => {
  if (value === undefined) {
    return null;
  }
  return dayjs(value).format('YYYY-MM');
};
const processYearMonthDay = (value?: string): string | null => {
  if (value === undefined) {
    return null;
  }
  return dayjs(value).format('YYYY-MM-DD');
};

const processYesNo = (value?: boolean): FINANCE_QUOTE_RADIO_TYPE | '' => {
  if (value === undefined) {
    return '';
  }
  return value ? FINANCE_QUOTE_RADIO_TYPE.YES_OPTION : FINANCE_QUOTE_RADIO_TYPE.NO_OPTION;
};

export const financeQuoteRequestPayloadToPersonalizedQuotePayloadPartial = (
  quotePayload: FinanceQuoteRequestPayload | null,
  payload: PersonalisedQuotePayloadPartial,
): PersonalisedQuotePayloadPartial => {
  const financeQuoteInfo: PersonalisedQuotePayloadPartial = { ...payload };
  if (quotePayload?.vehicle && quotePayload?.loan_amount_cents) {
    financeQuoteInfo.loanDetails = {
      depositAmount: (quotePayload.vehicle.price_cents - quotePayload.loan_amount_cents) / 100,
      estimatedPurchasePrice: quotePayload.vehicle.price_cents / 100,
      preferredBuildYear: quotePayload.vehicle.year,
    };
  }

  if (quotePayload?.applicant) {
    const persionalDetail = {
      abn: '',
      dateOfBirth: quotePayload.applicant.date_of_birth,
      firstName: quotePayload.applicant.first_name,
      lastName: quotePayload.applicant.last_name,
      middleName: quotePayload.applicant.middle_name,
      phone: quotePayload.applicant.phone,
      licenceNumber: quotePayload.applicant.driver_licence,
      isBusinessUseSelected: false,
    };

    if (quotePayload?.applicant.business) {
      financeQuoteInfo.loanType = { loanType: BUY_TYPE.BUSINESS };
      persionalDetail.abn = quotePayload.applicant.business.abn;
      persionalDetail.isBusinessUseSelected = true;
      financeQuoteInfo.personalDetails = persionalDetail;
    } else {
      financeQuoteInfo.loanType = { loanType: BUY_TYPE.PERSONAL };
      financeQuoteInfo.personalDetails = persionalDetail;
    }

    financeQuoteInfo.familyDetails = {
      address: financeAddressToPayloadAddress(quotePayload.applicant.current_address.address)!,
      currentAddressStartDate: processYearMonth(quotePayload.applicant.current_address.start_date) ?? ('' as any),
      familyStatus: quotePayload.applicant.personal?.marital_status ?? '',
      hasInvestmentProperty: processYesNo(quotePayload.applicant.has_investment_property),
      hasMortgageOnCurrentAddress: processYesNo(quotePayload.applicant.has_mortgage_on_current_address),
      hasMortgageOnInvestmentProperty: processYesNo(quotePayload.applicant.has_mortgage_on_investment_property),
      hasTenancyAgreement: processYesNo(quotePayload.applicant.personal?.has_tenancy_agreement),
      investmentPropertyAddress: quotePayload.applicant.personal?.investment_property
        ? (financeAddressToPayloadAddress(quotePayload.applicant.personal?.investment_property?.address) ?? {})
        : {},
      isBusinessUseSelected: persionalDetail.isBusinessUseSelected,
      previousAddressStartDate: processYearMonth(quotePayload.applicant.previous_address?.start_date) ?? ('' as any),
      previousResidentialAddress:
        financeAddressToPayloadAddress(quotePayload.applicant.previous_address?.address) ?? {},
      residentialStatus: quotePayload.applicant.current_address_status,
    };

    financeQuoteInfo.employmentAndCitizenshipDetails = {
      employmentStatus: quotePayload.applicant.personal?.current_employment.status ?? '',
      isBusinessUseSelected: persionalDetail.isBusinessUseSelected,
      jobTitle: quotePayload.applicant.personal?.current_employment.job_title ?? '',
      residencyStatus: quotePayload.applicant.residency_status as FINANCE_RESIDENCY_STATUS,
      startedEmployerDate:
        processYearMonth(quotePayload.applicant.personal?.current_employment.start_date) ?? ('' as any),
      totalYearlyIncomeBeforeTax: !isNil(quotePayload.applicant.personal?.income_cents)
        ? quotePayload.applicant.personal!.income_cents / 100
        : ('' as any),
      // ('' as any) Will be the initial value of the Form
      visaSubclassNumber: quotePayload.applicant.visa?.code ?? ('' as any),
      visaExpiryDate: processYearMonthDay(quotePayload.applicant.visa?.end_date) ?? ('' as any),
    };

    financeQuoteInfo.termsAndConditionsDetails = {
      isBusinessUseSelected: persionalDetail.isBusinessUseSelected,
      isCheckTermsAndConditions: TERMS_AND_CONDITIONS.CHECKED,
    };
  }

  return financeQuoteInfo;
};
